import Head from 'next/head'
import Link from 'next/link'
import Header from '../src/local/Header'
import ErrorBoundary from '../src/local/ErrorBoundary'
import { HolmiAppDarkFooterBrand, HolmiAppFullLogo } from '../src/local/HolmiAppLogo'
import s from '../styles/RedesignHome.module.css'

const features = [
  { icon: 'doc', title: 'Helyi hírek', desc: 'A te településed aktualitásai, határozatai, közleményei valós időben.' },
  { icon: 'cal', title: 'Események', desc: 'Ne maradj le semmilyen helyi programról, rendezvényről, közgyűlésről.' },
  { icon: 'push', title: 'Push értesítők', desc: 'Azonnal értesülsz, amikor valami fontos történik a közösségedben.' },
  { icon: 'pin', title: 'Helyalapú tartalom', desc: 'Csak a te lakóhelyed vonatkozó információit mutatjuk – semmi felesleges zaj.' },
  { icon: 'users', title: 'Közösségi kapcsolat', desc: 'Közvetlen vonal a polgárok és az önkormányzat között.' },
  { icon: 'monitor', title: 'Android & iOS', desc: 'Ingyenesen letölthető, bármilyen okostelefonon zökkenőmentesen működik.' },
]

const settlements = ['Üllő', 'Balatonszentgyörgy', 'Alsómocsolád', 'Gyömrő', 'Gádoros']

const govBenefits = [
  { title: 'Nagyobb elérés', desc: 'A közlemények eljutnak minden érdeklődő polgárhoz' },
  { title: 'Időtakarékos', desc: 'Egy felületen kezelhető az összes tartalom' },
  { title: 'Átlátható kommunikáció', desc: 'Bizalom erősítése a közösséggel való nyílt párbeszéddel' },
]

/* SVG icon lookup */
function FeatureIcon({ type }: { type: string }) {
  const p = { viewBox: "0 0 24 24", fill: "none", stroke: "#27AE78", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const }
  switch (type) {
    case 'doc': return <svg {...p}><path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l6 6v8a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
    case 'cal': return <svg {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
    case 'push': return <svg {...p}><path d="M18 8h1a4 4 0 010 8h-1" /><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" /><line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" /></svg>
    case 'pin': return <svg {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
    case 'users': return <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>
    case 'monitor': return <svg {...p}><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
    default: return null
  }
}

const CheckSvg = () => <svg viewBox="0 0 13 13" fill="none" stroke="#27AE78" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="2 6.5 5.5 10 11 3" /></svg>
const PinSvg = () => <svg viewBox="0 0 24 24" fill="#27AE78"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
const PlaySvg = () => <svg viewBox="0 0 24 24"><path d="M3.18 23.76a2 2 0 001.6-.19l12.4-7.2-2.93-2.93-11.07 10.32zM.42 1.92A2 2 0 000 3.08v17.84a2 2 0 00.42 1.16L.54 22.2 12.78 10 .54 1.8.42 1.92zM22.06 10.36L19.3 8.74l-3.23 3.24 3.23 3.24 2.79-1.62a2 2 0 000-3.24zM4.78.43L17.18 7.63l-2.93 2.93L4.18.63A2 2 0 014.78.43z" /></svg>
const AppleSvg = () => <svg viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>

export default function Home() {
  const currentYear = new Date().getFullYear()
  const yearsOnMarket = Math.max(1, currentYear - 2020)

  return (
    <ErrorBoundary>
      <div className={s.site}>
        <Head><title>HolmiApp – Települési applikáció</title></Head>
        <Header />

        {/* HERO */}
        <section className={s.hero}>
          <div>
            <div className={s.heroEyebrow}><span className={s.eyebrowDot} /> Települési applikáció</div>
            <h1 className={s.heroH1}>Találd meg, ami <em>téged érdekel</em></h1>
            <p className={s.heroSub}>Hírek, események, közérdekű információk – mind egy helyen. A helyi önkormányzatokkal együttműködve, a te közösségedért.</p>
            <div className={s.heroActions}>
              <Link href="/Bemutato" className={s.btnPrimary}>Tudj meg többet</Link>
              <Link href="/Telepulesek" className={s.btnSecondary}>Települések</Link>
            </div>
            <div className={s.storeBadges}>
              <a className={s.storeBadge} href="https://play.google.com/store/apps/details?id=hu.holmiapp" target="_blank" rel="noreferrer"><PlaySvg /> Google Play</a>
              <a className={s.storeBadge} href="https://apps.apple.com/hu/app/id1570622313" target="_blank" rel="noreferrer"><AppleSvg /> App Store</a>
            </div>
          </div>
          <div className={s.heroVisual}>
            <div className={s.pulseRing} /><div className={s.pulseRing} /><div className={s.pulseRing} />
            <div className={s.phoneWrap}>
              <div className={s.phone}>
                <div className={s.phoneNotch} />
                <div className={s.phoneScreen}>
                  <div className={s.phoneHeader}><HolmiAppFullLogo height={14} className={s.phoneLogoImg} /><div className={s.phoneBell}>🔔</div></div>
                  <div className={s.phoneCard}><div className={s.phoneCardLabel}>Hírek</div><div className={s.phoneCardTitle}>Közmeghallgatás a városházon</div><span className={s.phoneTag}>Esemény</span></div>
                  <div className={s.phoneCard}><div className={s.phoneCardLabel}>Közlemény</div><div className={s.phoneCardTitle}>Útfelújítás a Kossuth utcán</div><span className={s.phoneTag}>Infrastruktúra</span></div>
                  <div className={s.phoneCard}><div className={s.phoneCardLabel}>Közelben</div><div className={s.phoneCardTitle}>Könyvtár – nyitva tartás változás</div><span className={s.phoneTag}>Intézmény</span></div>
                  <div className={s.phoneStatRow}>
                    <div className={s.phoneStat}><div className={s.phoneStatNum}>5</div><div className={s.phoneStatLbl}>Település</div></div>
                    <div className={s.phoneStat}><div className={s.phoneStatNum}>200+</div><div className={s.phoneStatLbl}>Hír / hó</div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <div className={s.trustBar}>
          <span className={s.trustLabel}>Elérhető platformokon:</span>
          <div className={s.trustItems}>
            {['Android', 'iOS', 'Ingyenes letöltés', `${settlements.length} aktív település`, 'Önkormányzati partnerség'].map(t => (
              <span key={t} className={s.trustItem}><span className={s.trustDot} />{t}</span>
            ))}
          </div>
        </div>

        {/* FEATURES */}
        <section className={s.section}>
          <div className={s.sectionLabel}>Miért HolmiApp?</div>
          <h2 className={s.sectionTitle}>Minden helyi info egy helyen</h2>
          <p className={s.sectionSub}>Nem kell böngészni több weboldalt. Az önkormányzatokkal együttműködve hozzuk el neked, ami számít.</p>
          <div className={s.featuresGrid}>
            {features.map(f => (
              <div key={f.title} className={s.featureCard}>
                <div className={s.featureIcon}><FeatureIcon type={f.icon} /></div>
                <div className={s.featureTitle}>{f.title}</div>
                <div className={s.featureDesc}>{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* SETTLEMENTS */}
        <section className={s.settlementsSection}>
          <div className={s.sectionLabel}>Elérhető helyszínek</div>
          <h2 className={s.sectionTitle}>{settlements.length} aktív település</h2>
          <p className={s.sectionSub}>Jelenleg {settlements.length} települést látunk el tartalommal, és folyamatosan bővítjük a kört.</p>
          <div className={s.settlementsGrid}>
            {settlements.map(name => (
              <div key={name} className={s.settlementCard}>
                <div className={s.settlementIcon}><PinSvg /></div>
                <div className={s.settlementName}>{name}</div>
              </div>
            ))}
            <div className={s.settlementAdd}><div className={s.settlementAddIcon}>+</div><span>Hamarosan</span></div>
          </div>
        </section>

        {/* GOV SECTION */}
        <section className={s.section}>
          <div className={s.sectionLabel}>Önkormányzatoknak</div>
          <h2 className={s.sectionTitle}>Modern kommunikáció a polgárokkal</h2>
          <p className={s.sectionSub}>A HolmiApp közvetlen csatornát nyit az önkormányzat és a helyi közösség között – gyorsan, egyszerűen, hatékonyan.</p>
          <div className={s.govGrid}>
            <div className={s.govVisual}>
              <div className={s.govVisualTitle}>Hogyan működik?</div>
              <div className={s.govSteps}>
                {['Az önkormányzat csatlakozik a HolmiApp platformhoz', 'Tartalmakat, híreket és közleményeket tölt fel', 'A lakók valós időben értesülnek – push értesítőn keresztül'].map((txt, i) => (
                  <div key={i} className={s.govStep}><div className={s.govStepNum}>{i + 1}</div><div className={s.govStepText}>{txt}</div></div>
                ))}
              </div>
            </div>
            <div className={s.govBenefits}>
              {govBenefits.map(b => (
                <div key={b.title} className={s.govBenefit}>
                  <div className={s.govBenefitCheck}><CheckSvg /></div>
                  <div><div className={s.govBenefitTitle}>{b.title}</div><div className={s.govBenefitDesc}>{b.desc}</div></div>
                </div>
              ))}
              <div style={{ marginTop: '0.75rem' }}><Link href="/Onkormanyzat" className={s.btnPrimary}>Érdeklődjön a csatlakozásról</Link></div>
            </div>
          </div>
        </section>

        {/* STATS */}
        <section className={s.statsSection}>
          <div className={s.statsGrid}>
            <div className={s.statItem}><div className={s.statNum}>{settlements.length}<span>+</span></div><div className={s.statLabel}>Aktív település</div></div>
            <div className={s.statItem}><div className={s.statNum}><span>∞</span></div><div className={s.statLabel}>Bővítés folyamatban</div></div>
            <div className={s.statItem}><div className={s.statNum}>{yearsOnMarket}<span>+</span></div><div className={s.statLabel}>Éve a piacon</div></div>
          </div>
        </section>

        {/* CTA */}
        <section id="download" className={s.ctaSection}>
          <div className={s.sectionLabel} style={{ textAlign: 'center' }}>Töltsd le most</div>
          <h2 className={s.sectionTitle}>Légy mindig képben a helyi ügyekkel</h2>
          <p className={s.sectionSub}>Ingyenes applikáció, android és iOS rendszerre. Telepítés után válaszd ki a településed, és máris minden ott van.</p>
          <div className={s.ctaActions}>
            <a className={s.ctaStore} href="https://play.google.com/store/apps/details?id=hu.holmiapp" target="_blank" rel="noreferrer"><PlaySvg /> Google Play</a>
            <a className={s.ctaStore} href="https://apps.apple.com/hu/app/id1570622313" target="_blank" rel="noreferrer"><AppleSvg /> App Store</a>
          </div>
        </section>

        {/* FOOTER */}
        <footer className={s.footer}>
          <HolmiAppDarkFooterBrand brandClassName={s.footerBrand} textClassName={s.footerLogo} />
          <div className={s.footerLinks}>
            <Link href="/Bemutato">Bemutató</Link>
            <Link href="/Telepulesek">Települések</Link>
            <Link href="/Onkormanyzat">Önkormányzat</Link>
			<Link href="/Galeria">Galéria</Link>
          </div>
          <span className={s.footerCopy}>© {currentYear} HolmiApp. Minden jog fenntartva.</span>
        </footer>
      </div>
    </ErrorBoundary>
  )
}
